/**
 * Industry bucketing for the /employers industry sub-pages (dimensionConfig.ts industry entries).
 *
 * The Typesense `companies.industry` field (a.k.a. `industryEn`) does not hold coarse buckets like
 * "Technology". It holds the Finnish trade register's (PRH) TOL 2008 / NACE activity description
 * per company, e.g. "Computer programming activities" or "Freight transport by road". An industry
 * page therefore needs an explicit mapping from each bucket slug to the exact register strings
 * that belong to it, which is what this file is.
 *
 * Source: the `industry` facet of the prod `companies` collection among hiring companies
 * (`active_job_count:>0 || hasFinlandJobHistory:=true`), snapshot 2026-10-06 (361 distinct
 * values). Every value with at least 3 such companies was judged by hand, plus the tail of
 * values that share an unambiguous TOL prefix (e.g. "Manufacture of ..."). Judgement calls:
 * staffing / temporary employment / placement agencies, head offices and holding companies,
 * construction, hospitality, energy utilities and the like fit none of the nine buckets and are
 * left out on purpose; software publishing is technology, not media.
 *
 * Unmapped values simply don't appear on any industry page. New register values that show up
 * after the snapshot are ignored until someone adds them here (re-run the facet query and
 * extend the lists).
 */

export type IndustryBucketSlug =
  | 'technology'
  | 'finance'
  | 'healthcare'
  | 'retail'
  | 'manufacturing'
  | 'consulting'
  | 'education'
  | 'logistics'
  | 'media';

export const INDUSTRY_BUCKET_SLUGS: readonly IndustryBucketSlug[] = [
  'technology',
  'finance',
  'healthcare',
  'retail',
  'manufacturing',
  'consulting',
  'education',
  'logistics',
  'media',
];

export const INDUSTRY_BUCKET_VALUES: Record<IndustryBucketSlug, readonly string[]> = {
  technology: [
    "Computer programming activities",
    "Computer consultancy and computer facilities management activities",
    "Computing infrastructure, data processing, hosting and related activities",
    "Wired, wireless, and satellite telecommunication activities",
    "Other information technology and computer service activities",
    "Other information service activities",
    "Other software publishing",
  ],
  finance: [
    "Other activities auxiliary to financial services, except insurance and pension funding",
    "Other monetary intermediation",
    "Other financial service activities, except insurance and pension funding n.e.c.",
    "Activities of non-life insurance companies",
    "Activities of collection agencies and credit bureaus",
    "Fund management activities",
    "Other credit granting",
    "Activities of insurance agents and brokers",
    "Life insurance",
    "Security and commodity contracts brokerage",
  ],
  healthcare: [
    "Medical specialists activities",
    "Home help services for older persons and persons with disabilities",
    "Other human health activities n.e.c.",
    "Residential and professional family care activities for children and young people",
    "Residential care activities for older persons",
    "Other miscellaneous social work activities without accommodation n.e.c.",
    "Dental practice care activities",
    "Other miscellaneous residential care and housing activities n.e.c.",
    "General medical practice activities",
    "Residential nursing and care activities for persons with intellectual disabilities",
    "Residential care activities for persons with physical disabilities",
    "Residential nursing and care activities for mental health patients",
    "Hospital activities",
    "Residential nursing and care activities for substance abusers",
    "Residential nursing care activities",
    "Veterinary activities",
  ],
  retail: [
    "Non-specialised retail sale of predominately food, beverages or tobacco",
    "Wholesale of other machinery for use in industry",
    "Retail sale of cars and light motor vehicles",
    "Wholesale of information and communication equipment",
    "Retail sale of information and communication equipment",
    "Non-specialised wholesale trade",
    "Other non-specialised retail sale",
    "Wholesale of medical goods",
    "Retail sale of clothing without specialisation on any specific product group",
    "Retail sale of other new goods n.e.c.",
    "Wholesale of other machinery and equipment n.e.c.",
    "Wholesale of pharmaceutical goods",
    "Retail sale of other motor vehicle parts and accessories",
    "Wholesale of cars and light motor vehicles",
    "Wholesale of chemical products",
    "Wholesale of plumbing and heating equipment and supplies",
    "Retail sale of bicycles and other sports equipment",
    "Retail sale of pets and pet food",
    "Wholesale of clothing",
    "Wholesale of construction materials without specialisation on any specific product group",
    "Wholesale of electrical equipment and supplies",
    "Wholesale of hardware",
    "Wholesale of other household goods n.e.c.",
    "Wholesale of other motor vehicle parts and accessories",
    "Retail sale of beverages",
    "Retail sale of fabrics",
    "Retail sale of furniture",
    "Retail sale of hardware and building materials without specialisation on any specific product group",
    "Retail sale of health food",
    "Retail sale of motor vehicle tyres",
    "Retail sale of optical medical goods",
    "Retail sale of other tableware and household goods",
    "Wholesale of agricultural machinery, equipment and supplies",
    "Wholesale of health food",
    "Wholesale of lorries and other heavy motor vehicles",
    "Wholesale of metal construction materials",
    "Wholesale of mining, construction and civil engineering machinery",
    "Wholesale of other food n.e.c.",
    "Wholesale of stationery",
    "Wholesale of wood",
    "Non-specialised wholesale of food, beverages and tobacco",
    "Retail sale of automotive fuel",
    "Retail sale of books",
    "Retail sale of electrical household appliances",
    "Retail sale of motorcycles, motorcycle parts and accessories",
    "Retail sale of other cultural and recreational goods n.e.c.",
    "Retail sale of other plants and fertilisers",
    "Retail sale of other second-hand goods",
    "Retail sale of photographic goods",
    "Retail sale of touring caravans and other motor vehicles",
    "Retail sale of watches and jewellery",
    "Wholesale of fruit and vegetables",
    "Wholesale of household, office and shop furniture, carpets and lighting equipment",
    "Wholesale of machine tools",
    "Wholesale of metals and metal ores",
    "Wholesale of other intermediate products",
    "Wholesale of perfume and cosmetics",
    "Wholesale of radio and television equipment",
    "Wholesale of solid, liquid and gaseous fuels and related products",
    "Wholesale of sports goods",
    "Wholesale of tobacco products",
  ],
  manufacturing: [
    "Mechanical and process engineering design and technical consultancy",
    "Manufacture of instruments and appliances for measuring, testing and navigation",
    "Manufacture of other medical and dental instruments and supplies",
    "Electrical engineering design and technical consultancy",
    "Engineering design and technical consultancy incidental to urban planning",
    "Manufacture of lifting and handling equipment",
    "Manufacture of other special-purpose machinery n.e.c.",
    "Other engineering design and technical consultancy incidental to building construction",
    "Other technical testing and analysis",
    "Building of civilian ships and floating structures",
    "Manufacture of electricity distribution and control apparatus",
    "Manufacture of electronic components",
    "Manufacture of metal structures and parts of structures",
    "Manufacture of other electrical equipment",
    "Manufacture of other general-purpose machinery n.e.c.",
    "Manufacture of pharmaceutical preparations",
    "Other engineering activities and related technical consultancy",
    "Manufacture of other builders’ carpentry and joinery n.e.c.",
    "Manufacture of furniture",
    "Manufacture of machinery for mining, quarrying and construction",
    "Manufacture of non-domestic air conditioning equipment",
    "Manufacture of paper and paperboard",
    "Structural engineering design and technical consultancy",
    "Manufacture of agricultural and forestry machinery",
    "Manufacture of bearings, gears, gearing and driving elements",
    "Manufacture of concrete products for construction purposes",
    "Manufacture of electric motors, generators and transformers",
    "Manufacture of other chemical products n.e.c.",
    "Manufacture of other plastic products",
    "Engineering design and technical consultancy incidental to heating, plumbing and air-conditioning",
    "Machining of metals",
    "Manufacture of basic iron and steel and of ferro-alloys",
    "Manufacture of beer",
    "Manufacture of bread; manufacture of fresh pastry goods and cakes",
    "Manufacture of dairy products",
    "Manufacture of engines and turbines, except aircraft, vehicle and cycle engines",
    "Manufacture of irradiation, electromedical and electrotherapeutic equipment",
    "Manufacture of motor vehicles",
    "Manufacture of other food products n.e.c.",
    "Manufacture of other inorganic basic chemicals",
    "Manufacture of plastic packing goods",
    "Manufacture of plastic plates, sheets, tubes and profiles",
    "Manufacture of sports goods",
    "Production of meat and poultry meat products",
    "Repair and maintenance of general-purpose machinery",
    "Installation of industrial machinery and equipment",
    "Manufacture of bodies and coachwork for motor vehicles; manufacture of trailers and semi-trailers",
    "Manufacture of civilian air and spacecraft and related machinery",
    "Manufacture of communication equipment",
    "Manufacture of fertilisers and nitrogen compounds",
    "Manufacture of fluid power equipment",
    "Manufacture of lighting equipment",
    "Manufacture of machinery for food, beverage and tobacco processing",
    "Manufacture of machinery for paper and paperboard production",
    "Manufacture of other articles of concrete, cement and plaster",
    "Manufacture of other articles of paper and paperboard",
    "Manufacture of other organic basic chemicals",
    "Manufacture of other taps and valves",
    "Manufacture of other wearing apparel and accessories n.e.c.",
    "Manufacture of ovens, furnaces and permanent household heating equipment",
    "Manufacture of power-driven hand tools",
    "Manufacture of prefabricated wooden buildings",
    "Manufacture of weapons and ammunition",
    "Manufacture of workwear",
    "Repair and maintenance of agricultural and forestry machinery",
    "Repair and maintenance of household appliances and home and garden equipment",
    "Repair and maintenance of other special-purpose machinery",
    "Manufacture of abrasive products",
    "Manufacture of batteries and accumulators",
    "Manufacture of builders’ ware of plastic",
    "Manufacture of cider and other fermented fruit beverages",
    "Manufacture of cocoa, chocolate and sugar confectionery",
    "Manufacture of computers and peripheral equipment",
    "Manufacture of consumer electronics",
    "Manufacture of corrugated paper, paperboard and containers of paper and paperboard",
    "Manufacture of doors and windows of metal",
    "Manufacture of doors and windows of wood",
    "Manufacture of electrical and electronic equipment for motor vehicles",
    "Manufacture of fasteners and screw machine products",
    "Manufacture of footwear",
    "Manufacture of games and toys",
    "Manufacture of industrial gases",
    "Manufacture of jewellery and related articles",
    "Manufacture of loaded electronic boards",
    "Manufacture of locks and hinges",
    "Manufacture of malt",
    "Manufacture of metal forming machinery and machine tools for metal work",
    "Manufacture of mortars",
    "Manufacture of optical instruments, magnetic and optical media and photographic equipment",
    "Manufacture of other electronic and electric wires and cables",
    "Manufacture of other fabricated metal products n.e.c.",
    "Manufacture of other non-metallic mineral products n.e.c.",
    "Manufacture of other parts and accessories for motor vehicles",
    "Manufacture of other pumps and compressors",
    "Manufacture of other rubber products",
    "Manufacture of other textiles n.e.c.",
    "Manufacture of other transport equipment n.e.c.",
    "Manufacture of paints, varnishes and similar coatings, printing ink and mastics",
    "Manufacture of perfume and toilet preparations",
    "Manufacture of prepared meals and dishes",
    "Manufacture of ready-mixed concrete",
    "Manufacture of tubes, pipes, hollow profiles and related fittings, of steel",
    "Repair and maintenance of civilian air and spacecraft",
    "Repair and maintenance of computers and communication equipment",
    "Repair and maintenance of fabricated metal products",
    "Repair and maintenance of other civilian transport equipment",
  ],
  consulting: [
    "Business and other management consultancy activities",
    "Accounting and bookkeeping activities; tax consultancy",
    "All other miscellaneous professional, scientific and technical activities n.e.c.",
    "Auditing activities",
    "Legal representation activities",
    "Legal counselling and advising activities",
    "Translation and interpretation activities",
    "Architectural activities",
    "Legal counselling and advising activities on patents",
  ],
  education: [
    "Tertiary education",
    "Research and experimental development on natural sciences and engineering",
    "Other miscellaneous educational n.e.c.",
    "Vocational secondary education",
    "Pre-primary education",
    "Cultural education",
    "Driving school activities",
    "Educational support activities n.e.c.",
    "Sports and recreation education",
  ],
  logistics: [
    "Freight transport by road",
    "Logistics service activities",
    "Scheduled passenger transport by road",
    "Operation of harbours",
    "Other postal and courier activities",
    "Service activities incidental to air transportation",
    "On-demand passenger transport service activities by vehicle with driver",
    "Other service activities incidental to water transportation",
    "Sea passenger water transport",
    "Removal services",
    "Scheduled passenger air transport",
    "Warehousing and storage",
    "Cargo handling",
    "Sea and coastal freight water transport",
  ],
  media: [
    "Other activities of advertising agencies",
    "Publishing of newspapers",
    "Other printing",
    "Public relations and communication activities",
    "Television programming, broadcasting and video distribution activities",
    "Publishing of other journals and periodicals",
    "Market research and public opinion polling",
    "Motion picture, video and television programme post-production activities",
    "Other publishing activities, except software publishing",
    "Printing of newspapers",
  ],
};

/** Same backtick escaping as escapeTypesenseValue in fetch-best-companies-hub-page.ts. */
function escapeTypesenseValue(value: string): string {
  return value.replace(/`/g, '\\`');
}

export function isIndustryBucketSlug(slug: string): slug is IndustryBucketSlug {
  return Object.prototype.hasOwnProperty.call(INDUSTRY_BUCKET_VALUES, slug);
}

/** Typesense filter clause selecting every company whose `industry` is one of the bucket's values. */
export function buildIndustryBucketClause(slug: IndustryBucketSlug): string {
  const values = INDUSTRY_BUCKET_VALUES[slug].map((v) => `\`${escapeTypesenseValue(v)}\``);
  return `industry:=[${values.join(',')}]`;
}
